import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

// Mirrors GraphqlErrorProcessor's mark. Read through the global symbol registry rather than
// importing `isErrorMarkedAsHandled`, because ~20 test files mock
// `@confluence/graphql-error-processor` partially and a new import here would break all of them.
// The registry is the same mechanism GraphqlErrorProcessor relies on to share the mark across the
// preloader and main-app bundles, and `ignores errors already marked as handled` in this module's
// tests exercises the real `markErrorAsHandled`, so the key cannot drift unnoticed.
const HANDLED_ERROR_MARK_SYMBOL = Symbol.for('error marked as handled');

function isErrorMarkedAsHandled(error: unknown): boolean {
	return (
		typeof error === 'object' &&
		error !== null &&
		Boolean((error as Record<symbol, unknown>)[HANDLED_ERROR_MARK_SYMBOL])
	);
}

function expandErrorToErrors(error: any): Error[] {
	// Error from createDataFetcherForSuspense might have nested errors
	const errors: Error[] = [error];
	if (Array.isArray(error.otherErrors)) {
		errors.push(...error.otherErrors);
	}
	delete error.otherErrors;
	return errors;
}

type PreloaderQueryResult = {
	hasErrors: boolean;
	errors?: ReadonlyArray<unknown>;
};

/**
 * `query()` and `queryAgg()` resolve to `{ data, errors, hasErrors }` rather than rejecting or
 * resolving to an `Error`, so a preloader that returns its result object straight into the task
 * list would otherwise slip past the `instanceof Error` checks below.
 */
function isPreloaderQueryResultWithErrors(value: unknown): value is PreloaderQueryResult {
	return (
		typeof value === 'object' &&
		value !== null &&
		(value as PreloaderQueryResult).hasErrors === true &&
		Array.isArray((value as PreloaderQueryResult).errors)
	);
}

/**
 * Only surfaces errors nobody has claimed. `query()` marks the expected ones (unauthorized,
 * not-found, and other known shapes) as handled before resolving, and those still come back with
 * `hasErrors: true` — reporting them here would turn every restricted or missing page into a bogus
 * SSR prepare error, since preloaders like `preloadPageStatusQuery` return their result object
 * straight into the task list.
 */
function collectUnhandledQueryErrors(result: PreloaderQueryResult): Error[] {
	const unhandledErrors: Error[] = [];
	for (const error of result.errors ?? []) {
		if (isErrorMarkedAsHandled(error)) {
			continue;
		}
		unhandledErrors.push(
			error instanceof Error
				? error
				: new Error(String((error as { message?: string })?.message ?? error)),
		);
	}
	return unhandledErrors;
}

function collectErrorsFromFulfilledValue(fulfilledValue: unknown): Error[] {
	if (fulfilledValue instanceof Error) {
		return expandErrorToErrors(fulfilledValue);
	}

	if (Array.isArray(fulfilledValue)) {
		return fulfilledValue.flatMap((value) =>
			value instanceof Error || isPreloaderQueryResultWithErrors(value)
				? collectErrorsFromFulfilledValue(value)
				: [],
		);
	}

	if (isPreloaderQueryResultWithErrors(fulfilledValue)) {
		return collectUnhandledQueryErrors(fulfilledValue);
	}

	return [];
}

/**
 * Wait for all preload tasks to settle.
 * @returns void if successful, or an array of errors if failed.
 */
export async function waitForAllReturnWhenError<T>(
	values: T | T[],
): Promise<void | Error[] | Awaited<T>[]> {
	if (!Array.isArray(values)) {
		values = [values];
	}
	return Promise.allSettled(values).then((results) => {
		const errors = [] as Error[];
		for (const result of results) {
			if (result.status === 'rejected') {
				errors.push(...expandErrorToErrors(result.reason));
			} else if (result.status === 'fulfilled') {
				errors.push(...collectErrorsFromFulfilledValue(result.value));
			}
		}
		if (errors.length > 0) {
			// Errors on preloader functions are not meant to break anything.
			// But we still want to know them...
			for (const error of errors) {
				ssrPrepareErrorsClient.submitError(
					new Error(`Error caught by waitForAllReturnWhenError: ${error.message}`),
				);
			}

			return errors;
		}
	});
}
